import { computed, ref, unref, type Ref } from 'vue';

import { currentLocale } from '@/plugins/i18n';
import { pagesService } from '@/services/pages.service';
import { useCommunityStore } from '@/store/communityStore';
import { parsePage, type Page } from '@/store/page.interface';

export function usePage(
  pageId: number | null | undefined | Ref<number | null | undefined>,
  slug: string | null | undefined | Ref<string | null | undefined>,
) {
  const communityStore = useCommunityStore();

  const page = ref<Page | null>(null);
  const isLoading = ref(true);

  const resolvedPageId = computed(() => unref(pageId) ?? null);
  const resolvedSlug = computed(() => {
    const value = unref(slug);
    return value?.trim() ? value : null;
  });

  const themeKey = computed(() => page.value?.themeKey || communityStore.community?.defaultPageThemeKey || 'default');
  const blocks = computed(() => page.value?.blocks ?? []);
  const isLocaleSupported = computed(() => {
    if (!page.value?.languageEnabled?.length) {
      return true;
    }
    return page.value.languageEnabled.includes(currentLocale());
  });

  const loadPage = async () => {
    isLoading.value = true;

    try {
      let dtoPage = null;

      if (resolvedPageId.value) {
        dtoPage = await pagesService.getPage(resolvedPageId.value);
      } else if (resolvedSlug.value) {
        dtoPage = await pagesService.getPageBySlug(resolvedSlug.value);
      }

      page.value = dtoPage ? parsePage(dtoPage) : null;
    } catch (error) {
      console.error('Failed to load page:', error);
      page.value = null;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    page,
    blocks,
    themeKey,
    isLocaleSupported,
    isLoading,
    currentLocale: computed(() => currentLocale()),
    loadPage,
  };
}
